import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import {
  Pagination,
  Stack,
  TablePagination,
  TableSortLabel,
  Avatar,
  Typography,
  Switch,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import Link from "@mui/material/Link";
import "../../MetaEntities.css";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EditTrafficSourceModal from "../TrafficSourceModal/EditTrafficSourceModal";
import { useSelector } from "react-redux";
import {
  setPage,
  setRowsPerPage,
  setSelectedRows,
  setSort,
} from "../../../../../features/metaEntitiesSlice";
import store from "../../../../../store";
import { useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { formatDate } from '../../../../../utils/helpers';

interface Props {
  tableHeadings: { [key: string]: string };
  setMetaTrafficSource: any;
  metaTrafficSource: any;
  selectedTrafficSource: any;
  setSelectedTrafficSource: any;
  oauthTokenHeader: any;
  loading: any;
}

const TrafficSourceTable = ({ tableHeadings, setMetaTrafficSource }: Props) => {
  const { selectedEntityType, metaEntityList, sort, page, rowsPerPage } =
    useSelector((state: any) => state?.metaEntity);
  const dispatch = useDispatch();
  const [sortedData, setSortedData] = React.useState<any[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedRowData, setSelectedRowData] = useState<any>(null); // State to store selected row data

  const headCells = Object.entries(tableHeadings);

  const handleChangePage = (_: unknown, newPage: number) => {
    store.dispatch(setPage(newPage));

    setSearchParams((prevParams) => {
      const updatedParams = new URLSearchParams(prevParams);
      updatedParams.set("page", newPage.toString());
      return updatedParams;
    });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const [openEditModal, setopenModal] = useState(false);
  const [trafficSourceName, SetTrafficSourceName] = useState(null);
  const [selectedShareuid, setSelectedSharedUid] = useState(null);
  const [trafficSourceData, setTrafficSourceData] = useState(null);


  const handleopenEditModal = (row: any) => {
    setopenModal(true);
    SetTrafficSourceName(row?.traffic_source_name);
    setSelectedSharedUid(row?.shared_uid);
    setTrafficSourceData(row);
  };

  const handlecloseEditModal = () => {
    setopenModal(false);
  };

  const filteredTraficSourceData = sortedData?.filter((tsource: any) => {
    const statusTrafficSourceName =
      selectedEntityType && selectedEntityType.length !== 0
        ? selectedEntityType.includes(tsource?.traffic_source_name)
        : true; // If no Asset is selected, treat it as a match

    return statusTrafficSourceName;
  });

  const handleHeaderClick = (header: any) => {
    if (!header.sortable) return; // Ignore click if column is not sortable
    dispatch(
      setSort({
        keytoSort: header.KEY,
        // Toggle sort direction if the same column is clicked, otherwise default to ascending
        direction:
          header.KEY === sort.keytoSort && sort.direction === "asc"
            ? "desc"
            : "asc",
      })
    );
  };

  const tableheaders = [
    {
      id: 0,
      KEY: "traffic_source_name",
      label: "Traffic Source Name",
      sortable: true,
    },
    { id: 1, KEY: "traffic_type", label: "Traffic Source Type", sortable: true },
    { id: 2, KEY: "count_as_click_in_out", label: "Include Clicks In Reports", sortable: true},
    { id: 3, KEY: "created", label: "Created on", sortable: true },
    { id: 4, KEY: "created_by", label: "Created By", sortable: true },
    { id: 4, KEY: "modified", label: "Last edited", sortable: true },
    { id: 5, KEY: "modified_by", label: "Edited By", sortable: true },
  ];

  const getSortedData = React.useCallback(
    (arrayToSort: any[]) => {
      if (!sort.keytoSort || !sort.direction) {
        return arrayToSort;
      }

      return arrayToSort.slice().sort((a, b) => {
        const keytoSort = sort.keytoSort;

        if (keytoSort === undefined) {
          return 0;
        }

        const aValue = a[keytoSort];
        const bValue = b[keytoSort];

        if (aValue === undefined) return 1;
        if (bValue === undefined) return -1;

        if (aValue === "") return 1;
        if (bValue === "") return -1;

        if (typeof aValue === "string" && typeof bValue === "string") {
          return sort.direction === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        } else if (typeof aValue === "number" && typeof bValue === "number") {
          return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
        } else {
          return sort.direction === "asc"
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue));
        }
      });
    },
    [sort]
  );

  const sortedRows = getSortedData(filteredTraficSourceData || []);
  const paginatedMetaTrafficSource = sortedRows?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  useEffect(() => {
    store.dispatch(setSelectedRows([]));
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const sortedRows = getSortedData(metaEntityList || []);
    setSortedData(sortedRows);
    //eslint-disable-next-line
  }, [metaEntityList, getSortedData]);

  useEffect(() => {
    if (page !== 0) {
      store.dispatch(setPage(0));
    }
    // eslint-disable-next-line
  }, [selectedEntityType]);

  useEffect(() => {
    const pageFromUrl = Number(searchParams.get("page")); // Convert to number
    store.dispatch(setPage(isNaN(pageFromUrl) ? 0 : pageFromUrl)); // Ensure it defaults correctly
  }, [searchParams]);

  return (
    <>
      <TableContainer className="PageScantablewrap" component={Paper}>
        <Table aria-label="collapsible table" size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {tableheaders.map((header: any) => (
                <TableCell key={header.id} align="left">
                  <TableSortLabel
                    active={sort.keytoSort === header.KEY}
                    direction={
                      sort.keytoSort === header.KEY ? sort.direction : "asc"
                    }
                    onClick={() => handleHeaderClick(header)}
                    disabled={!header.sortable}
                  >
                    {header.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedMetaTrafficSource.length > 0 &&
              paginatedMetaTrafficSource?.map((row, rowIndex) => (
                <React.Fragment key={rowIndex}>
                  <TableRow
                    sx={{
                      "& > *": {
                        borderBottom:
                          "1px solid rgba(224, 224, 224, 1) !important",
                      },
                    }}
                  >
                    {headCells.map(([key], cellIndex) => (
                      <TableCell key={cellIndex} align="left">
                        {key === "traffic_source_name" ? (
                          <Stack
                            flexDirection="row"
                            alignItems="center"
                            gap={1}
                          >
                            <Link href="#" color="secondary">
                              <EditOutlinedIcon
                                data-tracker="234"
                                onClick={() => handleopenEditModal(row)}
                                fontSize="small"
                              />
                            </Link>
                            <Typography
                              variant="body2"
                              sx={{ marginTop: "0 !important" }}
                            >
                              {row[key] ? row[key] : "N/A"}
                            </Typography>
                          </Stack>
                        ) : key === "created" ? (
                          <> {row[key] ? formatDate(row[key]) : "N/A"}</>
                        ) : key === "created_by" ? (
                          <Stack
                            flexDirection={"row"}
                            alignItems={"center"}
                            gap={1}
                          >
                            <Avatar
                              alt={row[key] ? row[key] : "N/A"}
                              src="#"
                              sx={{ width: 24, height: 24 }}
                            />
                            {row[key] ? row[key] : "N/A"}
                          </Stack>
                        ) : key === "modified" ? (
                          <>{row[key] ? formatDate(row[key]) : "N/A"}</>
                        ) : key === "modified_by" ? (
                          <Stack
                            flexDirection={"row"}
                            alignItems={"center"}
                            gap={1}
                          >
                            <Avatar
                              alt={row[key] ? row[key] : "N/A"}
                              src="#"
                              sx={{ width: 24, height: 24 }}
                            />
                            {row[key] ? row[key] : "N/A"}
                          </Stack>
                        ) : key === "count_as_click_in_out" ? (
                          <Stack
                            flexDirection={"row"}
                            alignItems={"center"}
                            gap={1}
                          >
                            <Switch checked={row[key]} color="success"/>
                            {row[key] === true ? "True" : "False"}
                          </Stack>
                        ) : (
                          row[key] ? row[key] : ""
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </React.Fragment>
              ))}
            {!paginatedMetaTrafficSource?.length && (
              <TableRow>
                <TableCell
                  className="text-center"
                  colSpan={headCells.length + 1}
                >
                  <div className="p-2">There were no pages.</div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <Stack
          spacing={1}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <TablePagination
            rowsPerPageOptions={[]}
            component="div"
            count={sortedRows?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              ".css-16c50h-MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
                {
                  display: "none",
                },
              ".MuiTablePagination-actions": {
                display: "none",
              },
              ".MuiTablePagination-toolbar": {
                paddingLeft: "16px",
              },
            }}
          />
          <Pagination
            count={Math.ceil(filteredTraficSourceData?.length / rowsPerPage)}
            showFirstButton
            showLastButton
            className="pagination-button"
            color="secondary"
            size="small"
            sx={{
              ".css-wjh20t-MuiPagination-ul": {
                justifyContent: "center",
              },
            }}
            onChange={(_, value) => handleChangePage(null, value - 1)}
            page={page + 1}
          />
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={sortedRows?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            classes={{
              displayedRows: "hidden-displayed-rows", // Apply the custom class
            }}
            sx={{
              ".MuiTablePagination-actions": {
                display: "none",
              },
              ".MuiTablePagination-input": {
                marginRight: "15px",
              },
            }}
          />
        </Stack>
        <EditTrafficSourceModal
          open={openEditModal}
          handleClose={handlecloseEditModal}
          trafficSourceName={trafficSourceName}
          SetTrafficSourceName={SetTrafficSourceName}
          selectedShareuid={selectedShareuid}
          setSelectedSharedUid={setSelectedSharedUid}
          selectedRowData={selectedRowData}
          setSelectedRowData={setSelectedRowData}
          setMetaTrafficSource={setMetaTrafficSource}
          trafficSourceData={trafficSourceData}
          setTrafficSourceData={setTrafficSourceData}
        />
      </TableContainer>
    </>
  );
};

export default TrafficSourceTable;
